import { Stack } from '@mui/material';
import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelectedOrganisation } from '../../contexts';
import { CustomAttributeType } from '../../enums';
import { ICustomAttributeConfiguration } from '../../models';
import { ControlledCheckbox } from '../controlled-checkbox/controlled-checkbox.component';
import { ControlledNumberInput } from '../controlled-number-input/controlled-number-input.component';
import { ControlledAutocomplete } from '../controlled-autocomplete/controlled-autocomplete.component';
import { ControlledInput } from '../controlled-input/controlled-input.component';
import { Box } from '@mui/system';

interface Props {
    customAttributeConfigurations: ICustomAttributeConfiguration[];
}

export const RegistrationInputs: FC<Props> = ({ customAttributeConfigurations }) => {
    const { t } = useTranslation();
    const { theme } = window.carity.environment;
    const { organisation } = useSelectedOrganisation();
    const { register } = useFormContext();

    const attributes = customAttributeConfigurations.map((configuration, index) => ({
        ...configuration,
        index,
    }));
    const booleanAttributes = attributes.filter(({ type }) => type === CustomAttributeType.BOOLEAN);
    const amountAttributes = attributes.filter(({ type }) => type === CustomAttributeType.AMOUNT);

    return (
        <Stack spacing={2}>
            <ControlledAutocomplete
                name={'supportType'}
                label={t('supportType')}
                options={organisation?.supportTypes || []}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(value, option) => value.id === option.id}
                defaultValue={null}
            />
            <ControlledInput
                name="comment"
                label={t('comment')}
                multiline
                fullWidth
                slotProps={{ htmlInput: { maxLength: 120 } }}
            />

            <Stack direction="row" flexWrap="wrap" gap={theme.name === 'antwerp' ? 1 : 2}>
                {amountAttributes?.map((attribute) => (
                    <Box key={attribute.id} sx={{ width: `${attribute.name.length + 2}ch` }}>
                        <input
                            {...register(`attributes.${attribute.index}.configurationId`)}
                            hidden
                            value={attribute.id}
                        />
                        <ControlledNumberInput
                            label={attribute.name}
                            name={`attributes.${attribute.index}.amountValue`}
                        />
                    </Box>
                ))}
            </Stack>

            {booleanAttributes?.length > 0 && (
                <Stack spacing={2}>
                    {booleanAttributes?.map((attribute) => (
                        <Box key={attribute.id}>
                            <input
                                {...register(`attributes.${attribute.index}.configurationId`)}
                                hidden
                                value={attribute.id}
                            />
                            <ControlledCheckbox
                                label={attribute.name}
                                name={`attributes.${attribute.index}.booleanValue`}
                                sx={{ py: 0 }}
                            />
                        </Box>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};
